import { useEffect, useMemo, useState } from 'react';
import CustomList from '../../../../core/components/core/Util/List/CustomList/CustomList';
import CustomListContainer from '../../../../core/components/core/Util/List/CustomList/CustomListContainer';
import { loadData } from '../../../../core/components/core/Util/List/CustomList/loadData';
import { getUserSession } from '../../../../core/util/core/Session';
import config from '../../../config/config';
import { chooseProfileFilter } from '../../../helpers/chooseProfileFilter';
import { useDispatch } from 'react-redux';
import sleep from 'sleep-promise';
import moment from 'moment';

function List() {
    const session = getUserSession();
    const now = moment().format('YYYY-MM-DD');
    const profileFilter = chooseProfileFilter(); 
    const pathFrontEnd = '/pcontasaccountspayable/';
    const [filterValues, setFilterValues] = useState({});
    const dispatch = useDispatch();
    const paths = useMemo(() => ({
        pathAdd: pathFrontEnd + 'add',
        pathEdit: pathFrontEnd + 'edit',
        pathView: pathFrontEnd + 'view',
        endpointDelete: `${config.endpoints.backend}pcontasaccountspayable/delete`,
        endpoint: config.endpoints.backend + 'pcontasaccountspayable/getall',
        endpointGetAllEntities: config.endpoints.backend + 'entities/getall',
        endpointGetAllPdcProjects: config.endpoints.backend + 'projects/getall',
        endpointGetAllPcontasCategories: config.endpoints.backend + 'pcontascategories/getall',
        endpointGetAllPcontasSubCategories: config.endpoints.backend + 'pcontassubcategories/getall',
        endpointGetAllOriginResources: config.endpoints.backend + 'pcontasoriginresources/getall'
    }), []);

    async function updateSession(){
        await sleep(10);
        dispatch({ type: 'UPDATE_SESSION', key: 'custom_list_reload', value: new Date() });
    }

    useEffect(() => {
        async function update(){
            await updateSession();
        }    

        update();
    }, [filterValues])

    const clearFilters = (target, filterObject) => {
        const filterName = target['name'];
        if(filterName === 'pdc_entity_id'){
            filterObject = {pdc_entity_id: target['value']};
            if(filterValues.value_start && filterValues.value_end){
                filterObject = {...filterObject, value_start: filterValues.value_start, value_end: filterValues.value_end};
            }
        }else if(filterName === 'pdc_project_id'){
            delete filterObject.pdc_pconta_origin_resource_id;
            delete filterObject.pdc_pconta_category_id;
            delete filterObject.pdc_pconta_sub_category_id;
        }else if(filterName === 'pdc_pconta_category_id'){
            delete filterObject.pdc_pconta_sub_category_id;
        }
        return filterObject;
    }

    const handleChange = async (element) => {
        const target = element['target'];
        let filters = {...filterValues, [target['name']]: target['value']};
        if(target.name === 'period'){
            delete filters.period;
            let firstMonthDate = moment().startOf('month').format('YYYY-MM-DD');
            
            if(!!target.value_end && filters.value_start !== ''){
                firstMonthDate = moment(target.value_end).startOf('month').format('YYYY-MM-DD');
            }
            
            filters.value_start = !!target.value_start ? target.value_start : firstMonthDate;
            filters.value_end = !!target.value_end ? target.value_end : now;
        }
        filters = clearFilters(target,filters);
        setFilterValues(filters)
        
        await updateSession();
    }

    const filters = useMemo(() => ({
        validateOnChange: true,
        showGoBackButton: false,
        showSubmitButton: false,
        update_key: `${filterValues.pdc_pconta_category_id}-${filterValues.pdc_project_id}-${filterValues.pdc_entity_id}-${filterValues.pdc_pconta_origin_resource_id}-${filterValues.pdc_pconta_sub_category_id}-${filterValues.value_start}-${filterValues.value_end}`,
        handleSubmit: loadData,
        fields: [
            {
                name: 'pdc_entity_id',
                type: 'select',
                label: 'Entidade',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllEntities,
                    label: 'corporate_name',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'pdc_project_id',
                type: 'select',
                label: 'Projeto',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllPdcProjects,
                    label: 'project_name',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter,
                            pdc_entity_id: !!filterValues.pdc_entity_id ? filterValues.pdc_entity_id : profileFilter.pdc_entity_id 
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'pdc_pconta_origin_resource_id',
                type: 'select',
                label: 'Origem dos Recursos',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllOriginResources,
                    label: 'description',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter,
                            ...filterValues
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'pdc_pconta_category_id',
                type: 'select',
                label: 'Categoria',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllPcontasCategories,
                    label: 'description',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter,
                            ...filterValues 
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'pdc_pconta_sub_category_id',
                type: 'select',
                label: 'Sub-Categoria',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllPcontasSubCategories,
                    label: 'description',
                    value: 'id',
                    params: {
                        filter: {
                            ...filterValues
                        }
                    }
                },
                validations: {
                    required: false
                },
                handleChange,
                columnSize: 3,
            },
            {
                name: 'period',
                name_start: 'value_start',
                name_end: 'value_end',
                type: 'date_period',
                label: 'Periodo',
                handleChange,
                validations: {
                    required: false,
                },
                columnSize: 3,
            },
        ],
        values: {
            pdc_entity_id: filterValues.pdc_entity_id,
            pdc_project_id: filterValues.pdc_project_id,
            pdc_pconta_origin_resource_id: filterValues.pdc_pconta_origin_resource_id,
            pdc_pconta_category_id: filterValues.pdc_pconta_category_id,
            pdc_pconta_sub_category_id: filterValues.pdc_pconta_sub_category_id,
            value_start: filterValues.value_start,
            value_end: filterValues.value_end
        }
    }), [paths, filterValues]);

    const list = useMemo(() => {
        let list = {
            labelTitle: 'Lista de contas a pagar PCONTAS',
            labelTitleButtonAdd: 'Incluir nova conta a pagar',
            fieldsShowOnDelete: ['document'],
            formFilters: filters,
            showPrintButtons: true,
            canDelete: true,
            canEdit: false,
            filter: {
                ...profileFilter,
                pdc_entity_id: !!filterValues.pdc_entity_id ? filterValues.pdc_entity_id : profileFilter.pdc_entity_id,
                pdc_project_id: filterValues.pdc_project_id,
                pdc_pconta_origin_resource_id: filterValues.pdc_pconta_origin_resource_id,
                pdc_pconta_category_id: filterValues.pdc_pconta_category_id,
                pdc_pconta_sub_category_id: filterValues.pdc_pconta_sub_category_id,
                value_start: filterValues.value_start,
                value_end: filterValues.value_end
            },
            columns: [
                {
                    name: 'document',
                    label: 'Documento',
                    width: '10%'
                },
                {
                    name: 'project_name',
                    label: 'Projeto Contábil',
                    width: '15%'
                },
                {
                    name: 'payment_date',
                    label: 'Data Pagamento',
                    width: '10%'
                },
                {
                    name: 'maturity_date',
                    label: 'Data Vencimento',
                    width: '10%'
                },
                {
                    name: 'payment_description',
                    label: 'Descrição do Pagamento',
                    width: '15%'
                },
                {
                    name: 'payment_value',
                    label: 'Valor a pagar',
                    width: '10%'
                },
                {
                    name: 'payment_gross_value',
                    label: 'Valor bruto',
                    width: '10%'
                },
                {
                    name: 'amount_paid',
                    label: 'Valor Pago',
                    width: '10%'
                },
                {
                    name: 'partion_text',
                    label: 'Parcela',
                    width: '10%'
                },
            ]
        }
    
        list = { ...list, ...paths };

        return list;

    },[paths, filters]);

    const content = useMemo(() => (
        <CustomList list={{ ...list, ...paths }} />
    ), [list, paths, filters]);

    const customListContainer = useMemo(() => (
        <CustomListContainer
            canAdd={false}
            pathAdd={paths.pathAdd}
            labelTitle={list.labelTitle}
            labelTitleButtonAdd={list.labelTitleButtonAdd}
            content={content}
        />
    ), [content, paths, filters, list]);

    return (
        <div>
            {customListContainer}
        </div>
    );
}
export default List;
